import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Homepage from './pages/Homepage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Pets from './pages/Pets';
import PetDetail from './pages/PetDetail';
import Exercises from './pages/Exercises';
import ExerciseDetail from './pages/ExerciseDetail';
import Programs from './pages/Programs';
import ProgramDetail from './pages/ProgramDetail';
import AIVet from './pages/AIVet';
import AIVetChat from './pages/AIVetChat';
import LoadingSpinner from './components/LoadingSpinner';
import TermsAndConditions from './components/TermsAndConditions';
import TermsModal from './components/TermsModal';
import toast from 'react-hot-toast';

const ProtectedRoute = ({ children }) => {
  const { user, loading, requiresTermsAcceptance } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // If user needs to accept terms, show the terms modal on all protected routes
  if (requiresTermsAcceptance) {
    return (
      <TermsRequiredWrapper>
        {children}
      </TermsRequiredWrapper>
    );
  }
  
  return children;
};

const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return user ? <Navigate to="/app/pets" /> : children;
};

// Component to show terms modal and block interaction
const TermsRequiredWrapper = ({ children }) => {
  const { acceptTerms } = useAuth();
  const [accepting, setAccepting] = useState(false);

  const handleAcceptTerms = async () => {
    setAccepting(true);
    const result = await acceptTerms();
    if (result.success) {
      toast.success('Terms accepted successfully!');
    } else {
      toast.error(result.error || 'Failed to accept terms');
      setAccepting(false);
    }
  };

  return (
    <>
      {/* Render children but block with modal */}
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <TermsModal 
        isOpen={true} 
        onClose={() => {}} // Prevent closing without accepting
        onAccept={handleAcceptTerms}
      />
      {accepting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <LoadingSpinner />
        </div>
      )}
    </>
  );
};

function AppContent() {
  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/login" element={
          <AuthRoute>
            <Login />
          </AuthRoute>
        } />
        <Route path="/register" element={
          <AuthRoute>
            <Register />
          </AuthRoute>
        } />
        <Route path="/app" element={
          <ProtectedRoute>
            <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50">
              <Layout />
            </div>
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="pets" element={<Pets />} />
          <Route path="pets/:id" element={<PetDetail />} />
          <Route path="exercises" element={<Exercises />} />
          <Route path="exercises/:id" element={<ExerciseDetail />} />
          <Route path="programs" element={<Programs />} />
          <Route path="programs/:id" element={<ProgramDetail />} />
          <Route path="virtual-vet" element={<AIVet />} />
          <Route path="virtual-vet/chat/:sessionId" element={<AIVetChat />} />
        </Route>
      </Routes>
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;